/**
 * staffArSession — where an instructor's OWN AR session id lives for a sketch they don't own.
 *
 * The project doc carries exactly one `arSessionId`, and the `arSessions` rule pins `ownerId`
 * write-once (see `firestore.rules`). So an instructor who opened a student's sketch cannot
 * publish to the student's session doc: the update is PERMISSION_DENIED the moment the student
 * has ever pressed AR. They get their own session document instead, created under their own
 * uid — which the existing `allow create` rule already permits, so nothing in the rules moves.
 *
 * That id is kept here, in `localStorage`, rather than on the project doc, because:
 *   - a new project-doc field has to be threaded through `toEditorProject` + the project
 *     reducers + a write-back, and a silent drop in any one of those layers mints a fresh
 *     session on every press (this exact bug has already happened once with `arSessionId`);
 *   - it is per-instructor-per-project state that nothing server-side ever reads; and
 *   - the QR only has to stay stable on the machine showing it.
 *
 * Losing an entry (new browser, cleared storage) costs one stale session doc and a re-scan,
 * never correctness. The student's own session is never read or written by this path.
 */

const PREFIX = 'stemarts.staffArSession.';

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    // Safari private mode and friends throw on access rather than returning null.
    return null;
  }
}

function keyFor(projectId: string, uid: string): string {
  return `${PREFIX}${uid}.${projectId}`;
}

/** The instructor's stored session id for this project, or null if they've never opened one. */
export function readStaffArSessionId(
  projectId: string,
  uid: string
): string | null {
  const store = storage();
  if (!store || !projectId || !uid) return null;
  try {
    return store.getItem(keyFor(projectId, uid)) || null;
  } catch (e) {
    return null;
  }
}

/** Remember `sessionId` so the next press reuses it. No-ops when storage is unavailable. */
export function rememberStaffArSessionId(
  projectId: string,
  uid: string,
  sessionId: string
): void {
  const store = storage();
  if (!store || !projectId || !uid || !sessionId) return;
  try {
    store.setItem(keyFor(projectId, uid), sessionId);
  } catch (e) {
    // Quota or private-mode failure: the id is simply regenerated next time.
  }
}
